import React from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Rocket, CheckCircle2, Users, MessageSquare, AlertCircle } from 'lucide-react';
import Button from './ui/Button';

const BetaWelcomePage = ({ user, onComplete }) => {
  const [loading, setLoading] = React.useState(false);

  const handleStartTesting = async () => {
    setLoading(true);
    try {
      // Activer l'accès complet pendant la phase bêta
      await setDoc(doc(db, 'users', user.uid), {
        subscriptionStatus: 'active',
        plan: 'beta_tester',
        betaTester: true,
        betaStartDate: new Date().toISOString()
      }, { merge: true });
      
      if (onComplete) onComplete();
    } catch (error) {
      console.error('Erreur activation bêta:', error);
      alert('Erreur lors de l\'activation. Veuillez réessayer.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        
        {/* Badge Bêta */}
        <div className="text-center mb-6 animate-in fade-in slide-in-from-top-4">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-6 py-2 rounded-full font-bold text-sm shadow-lg">
            <Rocket size={18} className="animate-bounce"/>
            VERSION BÊTA
          </div>
        </div>

        {/* Carte principale */}
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-slate-200 animate-in fade-in slide-in-from-bottom-4">
          
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-4">
              Bienvenue sur Ovrio ! 🎉
            </h1>
            <p className="text-lg text-slate-600">
              Vous faites partie des premiers testeurs de notre plateforme
            </p>
          </div>

          {/* Avantages bêta */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-8 border border-blue-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <CheckCircle2 className="text-green-500" size={24}/>
              En tant que bêta testeur, vous bénéficiez de :
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20}/>
                <span className="text-slate-700">
                  <strong>Accès gratuit complet</strong> à toutes les fonctionnalités pendant la phase de test
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20}/>
                <span className="text-slate-700">
                  <strong>Devis & Factures illimités</strong>
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20}/>
                <span className="text-slate-700">
                  <strong>Signature électronique</strong> intégrée
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20}/>
                <span className="text-slate-700">
                  <strong>Gestion d'équipe</strong> et multi-entreprises
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={20}/>
                <span className="text-slate-700">
                  <strong>Support prioritaire</strong> pour vos retours
                </span>
              </li>
            </ul>
          </div>

          {/* Ce qu'on attend de vous */}
          <div className="bg-amber-50 rounded-2xl p-6 mb-8 border border-amber-200">
            <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <MessageSquare className="text-amber-600" size={24}/>
              Votre mission en tant que testeur :
            </h3>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                Utiliser l'application pour vos vrais projets
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                Signaler les bugs ou comportements étranges
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                Proposer des améliorations
              </li>
              <li className="flex items-start gap-2">
                <span className="text-amber-600 font-bold">•</span>
                Inviter d'autres artisans/entreprises à tester
              </li>
            </ul>
          </div>

          {/* Note importante */}
          <div className="bg-slate-50 rounded-xl p-4 mb-8 border border-slate-200">
            <div className="flex gap-3">
              <AlertCircle className="text-blue-500 flex-shrink-0 mt-0.5" size={20}/>
              <div className="text-sm text-slate-600">
                <strong className="text-slate-800">Note :</strong> Vos données sont sauvegardées en toute sécurité. 
                Cette phase de test nous permet d'améliorer Ovrio avant le lancement officiel.
              </div>
            </div>
          </div>

          {/* Bouton de démarrage */}
          <div className="text-center">
            <Button
              onClick={handleStartTesting}
              disabled={loading}
              className="px-8 py-4 text-lg shadow-2xl shadow-blue-500/30 hover:shadow-blue-500/50 transition-all"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Activation en cours...
                </>
              ) : (
                <>
                  <Rocket size={20} className="mr-2"/>
                  Commencer à tester Ovrio
                </>
              )}
            </Button>
            
            <p className="text-xs text-slate-400 mt-4">
              En continuant, vous acceptez de participer à la phase de test bêta
            </p>
          </div>

        </div>

        {/* Contact */}
        <div className="text-center mt-6 text-slate-600 text-sm">
          <p>
            Des questions ? Besoin d'aide ?{' '}
            <button 
              onClick={() => window.open('mailto:support@ovrio.net', '_blank')}
              className="text-blue-600 hover:text-blue-700 font-medium underline"
            >
              Contactez-nous
            </button>
          </p>
        </div>

      </div>
    </div>
  );
};

export default BetaWelcomePage;
